import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FaCamera, FaCheck, FaCloudUploadAlt, FaMapMarkerAlt, FaSpinner, FaTimes } from 'react-icons/fa';
import {
  resolveShopGpsLocation,
  SHOP_GPS_AUTO_FILL_ACCURACY_M,
  SHOP_GPS_UNRELIABLE_ACCURACY_M,
  SHOP_GPS_PRECISE_ACCURACY_M,
  SHOP_GPS_MOBILE_ACCEPT_ACCURACY_M,
  formatGpsAccuracyMeters,
  lookupPincodeCoordinates,
  isValidGeoCoord,
  logShopGeo,
} from '../../utils/geo';
import {
  getSpecificationsForAccount,
  getCompanyTypeOption,
  getCompanyTypesForAccount,
  normalizeBusinessSpecifications,
  isRegisteredCompanyType,
  normalizePin,
  reverseGeocodePinFromCoords,
  validateBusinessSubStep,
  composeFullAddress,
  geocodeShopAddress,
  resolveShopCoordinatesFromForm,
  hasMinimumShopAddress,
  getPinLocationCheck,
  verifyPinLocationCheck,
  isShopGpsUnreliable,
  getBusinessErrorFieldId,
  focusSignupField,
  isSellerCompanySignup,
  loadSignupSubStep,
  saveSignupSubStep,
  markSignupSessionActive,
  scrollSignupPanelToTop,
  hasPartnerFileField,
} from './partnerSignupBusiness';
import PartnerLocationFallback from './PartnerLocationFallback';
import SpecificationsDropdown from './SpecificationsDropdown';
import GarageClusterSelector, { getDefaultClustersOnTierChange } from './GarageClusterSelector';
import GarageFulfillmentFlags from './GarageFulfillmentFlags';
import GarageServicePreview from './GarageServicePreview';
import GarageTagSelector from './GarageTagSelector';
import {
  normalizeGarageTier,
  GARAGE_CAPABILITY_TAGS,
  GARAGE_PROJECT_TAGS,
} from '../../constants/garageServiceClusters';
import { INDIAN_BANKS } from '../../data/indianBanks';
import {
  prepareStoredUploadFile,
  prepareMobileCameraUpload,
  uploadPreparedFile,
  getUploadErrorMessage,
  shouldDeferSignupUpload,
  shouldUseMobileCameraDirectUpload,
  normalizeCaptureFile,
  recoverCameraMemory,
  MAX_CAMERA_FILE_BYTES,
  MAX_RAW_CAMERA_BYTES,
  prefersMobileDevice,
} from './partnerUploadFile';
import { savePartnerFile, deletePartnerFile, getPartnerFile, getPartnerFilePreview } from './partnerSignupFileStore';
import {
  registerSignupPreviewRevoker,
  releaseAllSignupPreviews,
  beginSignupFileProcessing,
  endSignupFileProcessing,
  isSignupFileProcessing,
  shouldHideCameraPreview,
} from './partnerSignupPreviewMemory';
import { getDocumentUrl } from '../../services/apiService';
const SUB_STEPS = ['Location', 'Business', 'Documents'];

/** Marks address + PIN verification path (desktop / weak GPS). Must exceed SHOP_GPS_UNRELIABLE_ACCURACY_M. */
const MANUAL_LOCATION_ACCURACY_M = 200000;

function isLikelyDesktopDevice() {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua)) {
    return false;
  }
  const finePointer = window.matchMedia?.('(pointer: fine)').matches;
  const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
  if (finePointer && !coarsePointer) return true;
  return true;
}

/** Browser geolocation requires a secure context (HTTPS), except localhost. */
function canUseLiveGps() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname || '';
  if (/^(localhost|127\.0\.0\.1)$/i.test(host)) return true;
  return Boolean(window.isSecureContext);
}

function getHttpsSetupHint() {
  if (typeof window === 'undefined') return '';
  if (canUseLiveGps()) return '';
  const host = window.location.hostname || '';
  if (/^(localhost|127\.0\.0\.1)$/i.test(host)) return '';
  return `On the PC run: npm run dev:host — then open the https:// URL shown under "On Your Network" (accept the certificate warning).`;
}

function getPreferredHttpsUrl() {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname || '';
  if (!host || /^(localhost|127\.0\.0\.1)$/i.test(host)) return '';
  const port = window.location.port && window.location.port !== '443'
    ? window.location.port
    : '3001';
  return `https://${host}:${port}`;
}

function getUploadPreviewSrc(value) {
  if (!value) return null;
  if (value.dataUrl && String(value.dataUrl).startsWith('data:')) return value.dataUrl;
  if (value.uploadedUrl && (value.mimeType || '').startsWith('image/')) return value.uploadedUrl;
  if (value.base64 && (value.mimeType || '').startsWith('image/')) {
    return `data:${value.mimeType};base64,${value.base64}`;
  }
  const name = value.previewName || value.fileName || '';
  if (value.base64 && /\.(jpe?g|png|gif|webp)$/i.test(name)) {
    return `data:image/jpeg;base64,${value.base64}`;
  }
  return null;
}

function isImageUpload(value) {
  if (!value) return false;
  if ((value.mimeType || '').startsWith('image/')) return true;
  const name = value.previewName || value.fileName || '';
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(name);
}

function inferMimeFromFileName(name) {
  if (!name) return '';
  if (/\.pdf$/i.test(name)) return 'application/pdf';
  if (/\.jpe?g$/i.test(name)) return 'image/jpeg';
  if (/\.png$/i.test(name)) return 'image/png';
  if (/\.webp$/i.test(name)) return 'image/webp';
  if (/\.gif$/i.test(name)) return 'image/gif';
  return '';
}

function acceptsImages(accept) {
  return String(accept).includes('image');
}

let previewLoadQueue = Promise.resolve();

function queuePreviewLoad(task) {
  const next = previewLoadQueue.then(task, task);
  previewLoadQueue = next.catch(() => {});
  return next;
}

function LocalFileField({
  label,
  value,
  onChange,
  accept = 'image/*,.pdf',
  required,
  fieldId,
  errorMessage,
  cameraOnly = false,
}) {
  const inputRef = useRef(null);
  const previewUrlRef = useRef(null);
  const lastPreviewKeyRef = useRef('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const processingRef = useRef(false);
  const hideImagePreview = shouldHideCameraPreview(cameraOnly);
  const mobileCameraDirect = shouldUseMobileCameraDirectUpload(cameraOnly && acceptsImages(accept));
  const chooseLabel = cameraOnly
    ? 'Take photo'
    : accept.includes('.pdf')
      ? 'Choose file or PDF'
      : 'Choose from gallery';
  const showError = errorMessage || err;

  const revokePreviewUrl = () => {
    if (previewUrlRef.current && previewUrlRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    previewUrlRef.current = null;
    setPreviewUrl(null);
    lastPreviewKeyRef.current = '';
  };

  useEffect(() => registerSignupPreviewRevoker(revokePreviewUrl), []);

  useEffect(() => {
    let cancelled = false;

    async function loadPreview() {
      if (hideImagePreview) {
        revokePreviewUrl();
        return;
      }
      if (!value) {
        revokePreviewUrl();
        return;
      }
      if (value.uploadedUrl && isImageUpload(value)) {
        setPreviewUrl(getDocumentUrl(value.uploadedUrl));
        return;
      }
      if (value.base64) {
        setPreviewUrl(getUploadPreviewSrc(value));
        return;
      }
      if (value.storedInDb && value.storageKey) {
        if (
          previewUrlRef.current
          && lastPreviewKeyRef.current === value.storageKey
        ) {
          return;
        }

        await queuePreviewLoad(async () => {
          if (cancelled) return;
          try {
            const thumb = await getPartnerFilePreview(value.storageKey);
            if (cancelled) return;
            if (thumb) {
              if (previewUrlRef.current?.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrlRef.current);
              }
              const url = URL.createObjectURL(thumb);
              previewUrlRef.current = url;
              lastPreviewKeyRef.current = value.storageKey;
              setPreviewUrl(url);
              return;
            }

            const file = await getPartnerFile(value.storageKey);
            if (cancelled || !file) return;
            if (!isImageUpload(value) || file.size > MAX_CAMERA_FILE_BYTES) {
              setPreviewFailed(true);
              return;
            }

            if (previewUrlRef.current?.startsWith('blob:')) {
              URL.revokeObjectURL(previewUrlRef.current);
            }
            const url = URL.createObjectURL(file);
            previewUrlRef.current = url;
            lastPreviewKeyRef.current = value.storageKey;
            setPreviewUrl(url);
          } catch {
            if (!cancelled) setPreviewFailed(true);
          }
        });
        return;
      }
      revokePreviewUrl();
    }

    loadPreview();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.storageKey, value?.storedInDb, value?.uploadedUrl, value?.base64, value?.fileName, hideImagePreview]);

  useEffect(() => () => revokePreviewUrl(), []);

  const resetInput = () => {
    if (inputRef.current) inputRef.current.value = '';
  };

  const pause = (ms = 0) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleFile = async (file) => {
    if (!file || !fieldId || processingRef.current) return;
    if (!beginSignupFileProcessing()) {
      setErr('Please wait for the previous photo to finish processing.');
      return;
    }
    processingRef.current = true;
    setErr('');
    setPreviewFailed(false);
    releaseAllSignupPreviews();
    setLoading(true);

    const isCameraCapture = cameraOnly && acceptsImages(accept);
    const mobileDirect = shouldUseMobileCameraDirectUpload(isCameraCapture);
    const deferUpload = shouldDeferSignupUpload({ cameraOnly: isCameraCapture });

    try {
      if (mobileDirect) {
        // Free previous camera buffers before touching the new capture.
        await recoverCameraMemory(900);
        if (file.size > MAX_RAW_CAMERA_BYTES) throw new Error('too_large');

        const prepared = await prepareMobileCameraUpload(file);
        const mimeType = prepared.type || inferMimeFromFileName(prepared.name) || 'image/jpeg';
        markSignupSessionActive();

        let uploadedUrl = '';
        try {
          uploadedUrl = await uploadPreparedFile(prepared);
        } catch (uploadErr) {
          try {
            await savePartnerFile(fieldId, prepared, null);
            onChange({
              fileName: prepared.name,
              mimeType,
              previewName: prepared.name,
              storageKey: fieldId,
              storedInDb: true,
            });
          } catch {
            const fallback = normalizeCaptureFile(file, { cameraOnly: true });
            await savePartnerFile(fieldId, fallback, null);
            onChange({
              fileName: fallback.name,
              mimeType: fallback.type || mimeType,
              previewName: fallback.name,
              storageKey: fieldId,
              storedInDb: true,
            });
          }
          setErr(getUploadErrorMessage(uploadErr));
          return;
        }

        await deletePartnerFile(fieldId).catch(() => {});
        // Drop local blob ASAP — keep only the CDN/server URL.
        onChange({
          fileName: prepared.name,
          mimeType,
          previewName: prepared.name,
          uploadedUrl,
        });
        await recoverCameraMemory(800);
        return;
      }

      await pause(isCameraCapture && prefersMobileDevice() ? 500 : isCameraCapture ? 250 : 0);
      const { file: prepared, previewFile } = await prepareStoredUploadFile(file, {
        cameraOnly: isCameraCapture,
      });
      const mimeType = prepared.type || inferMimeFromFileName(prepared.name) || 'image/jpeg';

      markSignupSessionActive();
      await savePartnerFile(fieldId, prepared, previewFile);

      if (!hideImagePreview) {
        const previewSource = previewFile || prepared;
        if (previewUrlRef.current?.startsWith('blob:')) {
          URL.revokeObjectURL(previewUrlRef.current);
        }
        const url = URL.createObjectURL(previewSource);
        previewUrlRef.current = url;
        lastPreviewKeyRef.current = fieldId;
        setPreviewUrl(url);
      } else {
        revokePreviewUrl();
      }

      let uploadedUrl = '';
      if (!deferUpload) {
        try {
          uploadedUrl = await uploadPreparedFile(prepared);
        } catch (uploadErr) {
          onChange({
            fileName: prepared.name,
            mimeType,
            previewName: prepared.name,
            storageKey: fieldId,
            storedInDb: true,
          });
          setErr(getUploadErrorMessage(uploadErr));
          return;
        }
      }

      onChange({
        fileName: prepared.name,
        mimeType,
        previewName: prepared.name,
        storageKey: fieldId,
        storedInDb: true,
        ...(uploadedUrl ? { uploadedUrl } : {}),
      });

      if (hideImagePreview) {
        await pause(400);
      }
    } catch (uploadErr) {
      setErr(getUploadErrorMessage(uploadErr));
    } finally {
      processingRef.current = false;
      endSignupFileProcessing();
      setLoading(false);
      resetInput();
    }
  };

  const onInputChange = (e) => {
    const picked = e.target.files?.[0];
    resetInput();
    handleFile(picked);
  };

  const openPicker = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSignupFileProcessing()) {
      setErr('Please wait for the previous photo to finish processing.');
      return;
    }
    if (cameraOnly && prefersMobileDevice()) {
      releaseAllSignupPreviews();
      await recoverCameraMemory(600);
    }
    inputRef.current?.click();
  };

  const clear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (fieldId) deletePartnerFile(fieldId).catch(() => {});
    revokePreviewUrl();
    setPreviewFailed(false);
    onChange(null);
    resetInput();
  };

  const hasFile = Boolean(
    value?.previewName
    || value?.fileName
    || value?.storedInDb
    || value?.uploadedUrl
    || value?.base64
  );
  const showImage = !hideImagePreview && hasFile && isImageUpload(value) && previewUrl && !previewFailed;
  const uploadOk = Boolean(value?.uploadedUrl) || (Boolean(value?.storedInDb) && !errorMessage && !err);
  const showSavedBadge = hideImagePreview && hasFile && isImageUpload(value) && !loading && uploadOk;

  return (
    <Field>
      <FieldLabel>{label}{required ? ' *' : ''}</FieldLabel>
      {cameraOnly && !hasFile && !loading && (
        <FieldHint>
          {mobileCameraDirect
            ? 'Take one photo at a time. Wait for "Photo saved" before the next capture.'
            : 'Take one photo at a time. Wait until compression finishes before the next capture.'}
        </FieldHint>
      )}
      <HiddenInput
        ref={inputRef}
        type="file"
        accept={cameraOnly ? 'image/*' : accept}
        capture={cameraOnly ? 'environment' : undefined}
        onChange={onInputChange}
      />
      <UploadZone $filled={hasFile} $hasError={Boolean(errorMessage)} id={fieldId} tabIndex={-1}>
        {hasFile ? (
          <UploadFilled>
            {showSavedBadge ? (
              <UploadSavedBadge>
                <FaCheck aria-hidden="true" />
                {value?.uploadedUrl ? 'Photo uploaded' : 'Photo saved'}
              </UploadSavedBadge>
            ) : showImage ? (
              <UploadPreview
                src={previewUrl}
                alt={value.previewName || value.fileName}
                onError={() => setPreviewFailed(true)}
              />
            ) : (
              <UploadFileIcon aria-hidden="true">
                <FaCloudUploadAlt />
              </UploadFileIcon>
            )}
            <UploadMeta>
              <UploadFileName>{value.previewName || value.fileName}</UploadFileName>
              <UploadMetaActions>
                <ChangeBtn type="button" onClick={openPicker}>
                  {cameraOnly ? <FaCamera size={10} /> : <FaCloudUploadAlt size={10} />}
                  {cameraOnly ? 'Retake' : 'Change'}
                </ChangeBtn>
                <ClearBtn type="button" onClick={clear}><FaTimes size={10} /> Remove</ClearBtn>
              </UploadMetaActions>
            </UploadMeta>
          </UploadFilled>
        ) : loading ? (
          <UploadRow>
            <FaSpinner className="fa-spin" />
            <span>
              {mobileCameraDirect ? 'Uploading photo…' : cameraOnly ? 'Compressing photo…' : 'Compressing & uploading…'}
            </span>
          </UploadRow>
        ) : (
          <UploadActions>
            <UploadActionBtn type="button" onClick={openPicker}>
              {cameraOnly ? <FaCamera /> : <FaCloudUploadAlt />}
              {chooseLabel}
            </UploadActionBtn>
          </UploadActions>
        )}
      </UploadZone>
      {showError && <FieldHint $error>{showError}</FieldHint>}
    </Field>
  );
}

function fe(id, fieldError) {
  const on = fieldError.id === id;
  return { $hasError: on, msg: on ? fieldError.message : '' };
}

function FieldErrorHint({ message }) {
  if (!message) return null;
  return <FieldHint $error>{message}</FieldHint>;
}

export default function PartnerBusinessForm({ form, onChange, onBack, onComplete, accountMainType = '' }) {
  const isGarageAccount = accountMainType === 'garage';
  const placeLabel = isGarageAccount ? 'garage' : 'shop';
  const [subStep, setSubStep] = useState(() => loadSignupSubStep());
  const [localError, setLocalError] = useState('');
  const [fieldError, setFieldError] = useState({ id: null, message: '' });
  const [locating, setLocating] = useState(false);
  const [locationNotice, setLocationNotice] = useState('');
  const [pinCheck, setPinCheck] = useState({ status: 'idle' });
  const [showLocationFallback, setShowLocationFallback] = useState(false);
  const [fallbackMapCenter, setFallbackMapCenter] = useState(null);
  const pinCheckRef = useRef(0);
  const isDesktop = isLikelyDesktopDevice();

  useEffect(() => {
    saveSignupSubStep(subStep);
  }, [subStep]);

  useEffect(() => {
    scrollSignupPanelToTop('partner-business-step-top');
  }, [subStep]);

  useEffect(() => {
    if (!isGarageAccount || !form.companyType) return;
    const tier = normalizeGarageTier(form.companyType);
    if (tier === 'large' && !(form.selectedClusters || []).length) {
      patch({ selectedClusters: getDefaultClustersOnTierChange(form.companyType) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGarageAccount, form.companyType]);

  useEffect(() => {
    if (subStep !== 0 || !isDesktop) return;
    if (
      form.locationAccuracyM
      || normalizePin(form.locationPinCode)
      || isValidGeoCoord(form.latitude, form.longitude)
    ) {
      return;
    }
    patch({ locationAccuracyM: String(MANUAL_LOCATION_ACCURACY_M) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subStep, isDesktop]);

  const openDesktopLocationFallback = (reason, approximateCenter = null) => {
    logShopGeo('fallback', 'Opening desktop location fallback', { reason, approximateCenter });
    if (
      approximateCenter
      && Number.isFinite(approximateCenter.lat)
      && Number.isFinite(approximateCenter.lng)
    ) {
      setFallbackMapCenter({ lat: approximateCenter.lat, lng: approximateCenter.lng });
    } else {
      setFallbackMapCenter(null);
    }
    patch({
      locationAccuracyM: String(MANUAL_LOCATION_ACCURACY_M),
      locationPinCode: '',
      locationPostOffice: '',
      // Keep any previously confirmed fallback coords; clear untrusted GPS
      ...(form.locationResolvedBy === 'fallback'
        ? {}
        : { latitude: '', longitude: '', capturedLocation: '', locationResolvedBy: '' }),
    });
    // Message lives in the modal — avoid duplicating it on the form behind,
    // unless the caller already set a specific HTTPS / error notice.
    if (reason !== 'https-required') {
      setLocationNotice('');
    }
    setShowLocationFallback(true);
  };

  const applyDesktopFallbackAddress = (addressPatch) => {
    const lat = Number(addressPatch.latitude);
    const lng = Number(addressPatch.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      setFallbackMapCenter({ lat, lng });
    }
    patch({
      ...addressPatch,
      locationAccuracyM: String(MANUAL_LOCATION_ACCURACY_M),
      locationResolvedBy: 'fallback',
    });
    setLocationNotice(
      `Location set from ${addressPatch.capturedLocation || 'map / address search'}. Confirm the fields below — we'll verify your PIN.`
    );
    setShowLocationFallback(false);
  };

  const showFieldError = (message, fieldId) => {
    if (fieldId) {
      setFieldError({ id: fieldId, message });
      setLocalError('');
    } else {
      setLocalError(message);
      setFieldError({ id: null, message: '' });
    }
    focusSignupField(fieldId);
  };

  const patch = (updates) => {
    const next = { ...form, ...updates };
    onChange(next);
    setLocalError('');
    setFieldError({ id: null, message: '' });
  };

  const applyResolvedCoordinates = async (resolved, notice, livePinOverride) => {
    const updates = {
      latitude: String(resolved.lat),
      longitude: String(resolved.lng),
      capturedLocation: resolved.label || composeFullAddress(form),
      locationResolvedBy: 'address',
    };
    if (livePinOverride) {
      updates.locationPinCode = normalizePin(livePinOverride);
    } else {
      try {
        const { pinCode, postOfficeName, state } = await reverseGeocodePinFromCoords(resolved.lat, resolved.lng);
        if (pinCode) updates.locationPinCode = pinCode;
        if (postOfficeName) updates.locationPostOffice = postOfficeName;
        if (state) updates.state = state;
      } catch {
        // PIN from coords unavailable
      }
    }
    patch(updates);
    if (notice) setLocationNotice(notice);
  };

  const captureLocation = async () => {
    setLocating(true);
    setLocalError('');
    setLocationNotice('');

    // Live GPS needs HTTPS (or localhost). Mobile keeps GPS-only UX — no map modal.
    if (!canUseLiveGps()) {
      logShopGeo('capture', 'Blocked live GPS — page is not a secure context', {
        href: typeof window !== 'undefined' ? window.location.href : '',
        isDesktop,
      });
      if (isDesktop) {
        setLocationNotice(
          `Live GPS needs HTTPS. ${getHttpsSetupHint() || `Set your ${placeLabel} on the map / address search.`}`
        );
        openDesktopLocationFallback('https-required');
      } else {
        const httpsUrl = getPreferredHttpsUrl() || 'https://YOUR-PC-IP:3001';
        setLocalError(
          `Live location needs HTTPS on your phone. Open ${httpsUrl} (accept the certificate warning), allow location, then try again.`
        );
      }
      setLocating(false);
      return;
    }

    try {
      const loc = await resolveShopGpsLocation();
      const lat = loc.lat;
      const lng = loc.lng;
      const accuracy = Math.round(loc.accuracy || 0);
      const accuracyLabel = formatGpsAccuracyMeters(accuracy);

      // Desktop-only: map / address search when GPS is missing or too coarse.
      if (isDesktop) {
        logShopGeo('capture', 'Opening desktop location fallback', {
          lat,
          lng,
          accuracy,
          source: loc.source,
          isDesktop: true,
        });
        openDesktopLocationFallback(
          loc.source === 'network'
            ? 'network-approximate'
            : accuracy > SHOP_GPS_PRECISE_ACCURACY_M
              ? 'accuracy-above-100m'
              : 'desktop-device',
          { lat, lng }
        );
        return;
      }

      // Mobile: always use the live GPS path (never open the map modal).
      const isNetworkFallback = loc.source === 'network';
      const unreliable = isNetworkFallback || accuracy > SHOP_GPS_UNRELIABLE_ACCURACY_M;
      const canAutoFillAddress = !isNetworkFallback && accuracy <= SHOP_GPS_AUTO_FILL_ACCURACY_M;
      logShopGeo('capture', 'Location resolved', {
        lat,
        lng,
        accuracy,
        source: loc.source,
        isNetworkFallback,
        unreliable,
        canAutoFillAddress,
      });

      if (isNetworkFallback) {
        setLocalError(
          `Phone gave a network location only (${accuracyLabel}), not GPS. Turn on Location / High accuracy, then tap Use live location again at your ${placeLabel}.`
        );
        return;
      }

      // Coarse GPS reverse-geocodes to the wrong village/area — reject and retry.
      if (accuracy > SHOP_GPS_MOBILE_ACCEPT_ACCURACY_M) {
        logShopGeo('capture', 'Rejecting coarse mobile GPS fix', { lat, lng, accuracy });
        patch({
          latitude: '',
          longitude: '',
          capturedLocation: '',
          locationPinCode: '',
          locationPostOffice: '',
          locationAccuracyM: '',
          locationResolvedBy: '',
        });
        setLocalError(
          `GPS is too approximate (${accuracyLabel}). Stand outdoors at your ${placeLabel}, turn on Precise / High accuracy location, wait 10–15 seconds, then tap Use live location again.`
        );
        return;
      }

      const updates = {
        latitude: String(lat),
        longitude: String(lng),
        locationAccuracyM: String(accuracy),
        locationResolvedBy: 'gps',
      };

      try {
        const { pinCode, city, locationLabel, postOfficeName, state } = await reverseGeocodePinFromCoords(lat, lng);
        if (pinCode) updates.locationPinCode = pinCode;
        if (postOfficeName) updates.locationPostOffice = postOfficeName;
        if (state) updates.state = state;
        // Only trust place names when accuracy is tight enough.
        if (canAutoFillAddress && city && !form.city?.trim()) updates.city = city;
        if (canAutoFillAddress && locationLabel) {
          updates.capturedLocation = locationLabel;
        } else {
          updates.capturedLocation = `GPS ${lat.toFixed(5)}, ${lng.toFixed(5)} (${accuracyLabel})`;
        }
      } catch {
        updates.capturedLocation = `GPS ${lat.toFixed(5)}, ${lng.toFixed(5)} (${accuracyLabel})`;
      }

      patch(updates);

      if (!canAutoFillAddress) {
        setLocationNotice(
          `Location saved (${accuracyLabel}). Enter your exact ${placeLabel} address and matching PIN below — do not rely on the map label alone.`
        );
      } else if (accuracy > 50) {
        setLocationNotice(`Location captured (${accuracyLabel}). Please confirm your ${placeLabel} address below.`);
      }
    } catch (err) {
      logShopGeo('capture', 'resolveShopGpsLocation threw', {
        code: err?.code,
        message: err?.message,
        accuracy: err?.accuracy,
      });

      if (isDesktop) {
        openDesktopLocationFallback('desktop-no-gps');
        return;
      }

      // Mobile: keep GPS-only recovery — never open the map modal.
      if (hasMinimumShopAddress(form)) {
        const fromAddress = await geocodeShopAddress(form);
        if (fromAddress) {
          await applyResolvedCoordinates(fromAddress, 'GPS unavailable — location set from your address.');
          return;
        }
      }

      const pin = normalizePin(form.pinCode);
      if (pin.length === 6) {
        try {
          const coords = await lookupPincodeCoordinates(pin);
          if (coords && isValidGeoCoord(coords.lat, coords.lng)) {
            await applyResolvedCoordinates(
              { lat: coords.lat, lng: coords.lng, label: `Near PIN ${pin}` },
              'GPS unavailable — saved approximate location from your PIN code.',
              pin
            );
            return;
          }
        } catch {
          // fall through to error
        }
      }

      const code = err?.code;
      if (code === 1) {
        setLocalError('Location permission denied. Allow location for this site, then tap Use live location again.');
      } else if (!canUseLiveGps()) {
        const httpsUrl = getPreferredHttpsUrl() || 'https://YOUR-PC-IP:3001';
        setLocalError(`Live location needs HTTPS. Open ${httpsUrl}, accept the warning, then try again.`);
      } else if (/accuracy too low/i.test(String(err?.message || '')) || Number(err?.accuracy) > SHOP_GPS_MOBILE_ACCEPT_ACCURACY_M) {
        const acc = formatGpsAccuracyMeters(err?.accuracy || 0);
        setLocalError(
          `GPS is too approximate${err?.accuracy ? ` (${acc})` : ''}. Stand outdoors at your ${placeLabel}, turn on Precise / High accuracy location, wait 10–15 seconds, then try again.`
        );
      } else {
        setLocalError(`Could not get GPS. Turn on Location / High accuracy at your ${placeLabel}, then try again.`);
      }
    } finally {
      setLocating(false);
    }
  };

  const handlePinCodeChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
    patch({ pinCode: digits });
    if (digits.length < 6) setPinCheck({ status: 'idle' });
  };

  useEffect(() => {
    const syncCheck = getPinLocationCheck(form);
    if (syncCheck.status !== 'checking') {
      setPinCheck(syncCheck);
      return undefined;
    }

    const requestId = ++pinCheckRef.current;
    const timer = setTimeout(async () => {
      const result = await verifyPinLocationCheck(form);
      if (pinCheckRef.current !== requestId) return;
      setPinCheck(result);
      if (result.status === 'valid' && result.state && result.state !== form.state) {
        patch({ state: result.state });
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [form.pinCode, form.city, form.locationPinCode, form.locationAccuracyM]);

  const handleField = (e) => {
    const { name, value } = e.target;
    if (name === 'shopName' && !isRegisteredCompanyType(form.companyType) && !isGarageAccount) {
      patch({ shopName: value, companyName: value });
      return;
    }
    if (name === 'companyType' && !isGarageAccount) {
      const nextType = value;
      if (!isRegisteredCompanyType(nextType)) {
        patch({
          companyType: nextType,
          companyName: form.shopName || form.companyName,
        });
        return;
      }
    }
    patch({ [name]: value });
  };

  const handleBankChange = (e) => {
    const bankName = e.target.value;
    patch({
      bankName,
      ...(bankName !== form.bankName ? { bankIfsc: '' } : {}),
    });
  };

  const handleGarageClusterPatch = (updates) => {
    patch(updates);
  };

  const handleSpecificationsPatch = (updates) => {
    patch(updates);
  };

  const selectedSpecifications = normalizeBusinessSpecifications(form.businessSpecifications);
  const hasOtherSpecification = selectedSpecifications.includes('other');
  const garageTier = normalizeGarageTier(form.companyType);
  const isCustomWorkshop = garageTier === 'custom_workshop';
  const isTransformer = garageTier === 'transformer';
  const isClusterGarage = garageTier && !isCustomWorkshop && !isTransformer;
  const hasLiveLocation = Boolean(
    normalizePin(form.locationPinCode)
    || isShopGpsUnreliable(form)
    || (String(form.capturedLocation || '').trim() && String(form.locationAccuracyM || '').trim() !== '')
  );

  const goNext = async () => {
    // Informal sellers: legal/business name follows shop name (no duplicate field).
    if (
      subStep === 1
      && !isGarageAccount
      && form.companyType
      && !isRegisteredCompanyType(form.companyType)
      && form.shopName?.trim()
      && form.companyName !== form.shopName
    ) {
      patch({ companyName: form.shopName });
    }

    const err = validateBusinessSubStep(subStep, {
      ...form,
      ...(!isGarageAccount
        && form.companyType
        && !isRegisteredCompanyType(form.companyType)
        && form.shopName?.trim()
        ? { companyName: form.shopName }
        : {}),
    }, accountMainType);
    if (err) {
      showFieldError(err, getBusinessErrorFieldId(err, subStep, form));
      return;
    }

    if (subStep === 0) {
      const pinCheckResult = await verifyPinLocationCheck(form);
      setPinCheck(pinCheckResult);
      if (pinCheckResult.status !== 'valid') {
        const pinMsg = pinCheckResult.message || 'Verify your PIN against live location.';
        showFieldError(pinMsg, 'pinCode');
        return;
      }

      if (isShopGpsUnreliable(form)) {
        const pin = normalizePin(form.pinCode);
        const keepFallbackCoords =
          form.locationResolvedBy === 'fallback'
          && isValidGeoCoord(form.latitude, form.longitude);
        const keepGpsCoords =
          form.locationResolvedBy === 'gps'
          && isValidGeoCoord(form.latitude, form.longitude);

        if (keepFallbackCoords || keepGpsCoords) {
          // Preserve search/map/GPS coordinates; still sync PIN for payload consistency.
          patch({
            locationPinCode: pin,
            capturedLocation: form.capturedLocation || composeFullAddress(form),
          });
        } else {
          const coords = await lookupPincodeCoordinates(pin);
          if (coords && isValidGeoCoord(coords.lat, coords.lng)) {
            patch({
              latitude: String(coords.lat),
              longitude: String(coords.lng),
              locationPinCode: pin,
              locationResolvedBy: 'pin',
              // Address label only — do not present as a live "captured" location.
              capturedLocation: '',
            });
          } else {
            const resolved = await resolveShopCoordinatesFromForm(form);
            if (!resolved) {
              showFieldError(
                `Could not locate your ${placeLabel} from PIN. Check your address details.`,
                'pinCode'
              );
              return;
            }
            patch({
              latitude: String(resolved.lat),
              longitude: String(resolved.lng),
              locationPinCode: pin,
              locationResolvedBy: 'address',
              capturedLocation: '',
            });
          }
        }
      } else {
        const hasLiveCoords = isValidGeoCoord(form.latitude, form.longitude)
          && (form.locationResolvedBy === 'gps' || form.locationResolvedBy === 'fallback');
        if (!hasLiveCoords) {
          const resolved = await resolveShopCoordinatesFromForm(form);
          if (resolved) {
            patch({
              latitude: String(resolved.lat),
              longitude: String(resolved.lng),
              locationResolvedBy: 'address',
              capturedLocation: '',
            });
          } else {
            showFieldError(
              `Could not locate your ${placeLabel}. Tap Use live location, or check your address details.`,
              'pinCode'
            );
            return;
          }
        }
      }
    }

    if (subStep < 2) {
      setSubStep((s) => s + 1);
      setLocalError('');
      return;
    }
    onComplete();
  };

  const goBack = () => {
    if (subStep > 0) {
      setSubStep((s) => s - 1);
      setLocalError('');
      setFieldError({ id: null, message: '' });
      return;
    }
    onBack();
  };

  const registeredType = isRegisteredCompanyType(form.companyType);
  const gstFieldId = registeredType ? 'gstNumberDocs' : 'gstOther';
  const selectedCompanyType = getCompanyTypeOption(form.companyType);
  const companyTypes = getCompanyTypesForAccount(accountMainType);
  const specificationOptions = getSpecificationsForAccount(accountMainType);
  const sellerCompanySignup = isSellerCompanySignup(form.companyType, accountMainType);
  const mobileDocFlow = prefersMobileDevice();
  const shopPictureDone = hasPartnerFileField(form.shopPicture);

  return (
    <Wrap>
      <SubStepBar id="partner-business-step-top">
        {SUB_STEPS.map((label, i) => (
          <SubStepItem key={label} $active={subStep === i} $done={subStep > i}>
            <SubDot $active={subStep === i} $done={subStep > i}>{i + 1}</SubDot>
            {label}
          </SubStepItem>
        ))}
      </SubStepBar>

      {localError && !fieldError.id && <ErrorBox>{localError}</ErrorBox>}

      {subStep === 0 && (
        <Panel key="loc">
          <SectionLabel>{isGarageAccount ? 'Garage address' : 'Shop address'}</SectionLabel>

          {!canUseLiveGps() && (
            <LocationNotice $https>
              <strong>Live location requires HTTPS.</strong>
              {' '}
              {isDesktop
                ? (getHttpsSetupHint() || 'Open this page over HTTPS, then tap Use live location.')
                : `On your phone open ${getPreferredHttpsUrl() || 'https://YOUR-PC-IP:3001'} — accept the certificate warning, allow location, then tap Use live location.`}
            </LocationNotice>
          )}

          {!hasLiveLocation && !isDesktop && canUseLiveGps() && (
            <LocationNotice>
              Tap <strong>Use live location</strong> at your {placeLabel} first, then confirm the address below.
            </LocationNotice>
          )}

          <LocationBtn type="button" onClick={captureLocation} disabled={locating} $top>
            {locating ? <FaSpinner className="fa-spin" /> : <FaMapMarkerAlt />}
            {locating
              ? 'Getting location…'
              : (isDesktop && !canUseLiveGps() ? 'Set location on map' : 'Use live location')}
          </LocationBtn>

          {form.latitude && form.longitude && form.capturedLocation
            && (form.locationResolvedBy === 'gps' || form.locationResolvedBy === 'fallback') && (
            <CapturedLocation>
              <FaMapMarkerAlt size={12} />
              <span>
                Captured location: <strong>{form.capturedLocation}</strong>
              </span>
            </CapturedLocation>
          )}

          {locationNotice && <LocationNotice>{locationNotice}</LocationNotice>}

          {isDesktop && (
            <PartnerLocationFallback
              open={showLocationFallback}
              onClose={() => setShowLocationFallback(false)}
              onApply={applyDesktopFallbackAddress}
              placeLabel={placeLabel}
              initialCenter={fallbackMapCenter}
            />
          )}

          <SectionLabel>Enter {placeLabel} address</SectionLabel>

          <FieldGrid $twoCol>
            <Field>
              <FieldLabel htmlFor="houseNo">{isGarageAccount ? 'House / garage no. *' : 'House / shop no. *'}</FieldLabel>
              <FieldInput id="houseNo" name="houseNo" value={form.houseNo} onChange={handleField} placeholder="e.g. 12, Plot 4" $hasError={fe('houseNo', fieldError).$hasError} />
              <FieldErrorHint message={fe('houseNo', fieldError).msg} />
            </Field>
            <Field>
              <FieldLabel htmlFor="street">Street *</FieldLabel>
              <FieldInput id="street" name="street" value={form.street} onChange={handleField} placeholder="Street / road name" $hasError={fe('street', fieldError).$hasError} />
              <FieldErrorHint message={fe('street', fieldError).msg} />
            </Field>
            <Field>
              <FieldLabel htmlFor="landmark">Landmark</FieldLabel>
              <FieldInput id="landmark" name="landmark" value={form.landmark} onChange={handleField} placeholder="Near metro, mall, etc." />
            </Field>
            <Field>
              <FieldLabel htmlFor="city">City *</FieldLabel>
              <FieldInput id="city" name="city" value={form.city} onChange={handleField} placeholder="City" $hasError={fe('city', fieldError).$hasError} />
              <FieldErrorHint message={fe('city', fieldError).msg} />
            </Field>
            <Field>
              <FieldLabel htmlFor="pinCode">PIN code *</FieldLabel>
              <FieldInput
                id="pinCode"
                name="pinCode"
                inputMode="numeric"
                maxLength={6}
                value={form.pinCode}
                onChange={handlePinCodeChange}
                placeholder="6-digit PIN code"
                $hasError={fe('pinCode', fieldError).$hasError || pinCheck.status === 'invalid' || pinCheck.status === 'error'}
              />
              {pinCheck.status === 'checking' && (
                <FieldHint>
                  <FaSpinner className="fa-spin" style={{ marginRight: 6 }} />
                  {pinCheck.message || 'Verifying PIN…'}
                </FieldHint>
              )}
              {pinCheck.status === 'valid' && (
                <FieldHint $success>{pinCheck.message}</FieldHint>
              )}
              <FieldErrorHint
                message={
                  fe('pinCode', fieldError).msg
                  || (pinCheck.status === 'invalid' || pinCheck.status === 'error' ? pinCheck.message : '')
                }
              />
            </Field>
          </FieldGrid>    
        </Panel>
      )}

      {subStep === 1 && (
        <Panel key="biz">
          {isGarageAccount ? (
            <>
              <SectionLabel>Garage registration</SectionLabel>
              <FieldGrid $twoCol>
                <Field>
                  <FieldLabel htmlFor="shopName">Garage name *</FieldLabel>
                  <FieldInput id="shopName" name="shopName" value={form.shopName} onChange={handleField} $hasError={fe('shopName', fieldError).$hasError} />
                  <FieldErrorHint message={fe('shopName', fieldError).msg} />
                </Field>
                <Field>
                  <FieldLabel htmlFor="shopNumber">Garage phone *</FieldLabel>
                  <FieldInput id="shopNumber" name="shopNumber" inputMode="numeric" maxLength={10} value={form.shopNumber} onChange={handleField} $hasError={fe('shopNumber', fieldError).$hasError} />
                  <FieldErrorHint message={fe('shopNumber', fieldError).msg} />
                </Field>
              </FieldGrid>

              <SectionLabel>Owner details</SectionLabel>
              <FieldGrid $twoCol>
                <Field>
                  <FieldLabel htmlFor="ownerName">Owner name *</FieldLabel>
                  <FieldInput id="ownerName" name="ownerName" value={form.ownerName} onChange={handleField} $hasError={fe('ownerName', fieldError).$hasError} />
                  <FieldErrorHint message={fe('ownerName', fieldError).msg} />
                </Field>
                <Field>
                  <FieldLabel htmlFor="aadhaarNumber">Owner Aadhaar number *</FieldLabel>
                  <FieldInput id="aadhaarNumber" name="aadhaarNumber" inputMode="numeric" maxLength={12} value={form.aadhaarNumber} onChange={handleField} $hasError={fe('aadhaarNumber', fieldError).$hasError} />
                  <FieldErrorHint message={fe('aadhaarNumber', fieldError).msg} />
                </Field>
                <Field $span2>
                  <FieldLabel htmlFor="panNumber">Owner PAN card number *</FieldLabel>
                  <FieldInput id="panNumber" name="panNumber" maxLength={10} value={form.panNumber} onChange={handleField} style={{ textTransform: 'uppercase' }} $hasError={fe('panNumber', fieldError).$hasError} />
                  <FieldErrorHint message={fe('panNumber', fieldError).msg} />
                </Field>
              </FieldGrid>

              <SectionLabel>Garage profile</SectionLabel>
              <FieldGrid $twoCol>
                {isClusterGarage && (
                  <Field $span2>
                    <GarageClusterSelector
                      tier={form.companyType}
                      selectedClusters={form.selectedClusters}
                      fuelSpecialistTypes={form.fuelSpecialistTypes}
                      onChange={handleGarageClusterPatch}
                      hasError={fe('selectedClusters', fieldError).$hasError}
                      errorMessage={fe('selectedClusters', fieldError).msg}
                    />
                  </Field>
                )}

                {isCustomWorkshop && (
                  <Field $span2>
                    <GarageTagSelector
                      fieldId="selectedCapabilityTags"
                      label="Capability tags"
                      hint="Custom workshop jobs are quote-based — no fixed service menu."
                      options={GARAGE_CAPABILITY_TAGS}
                      selected={form.selectedCapabilityTags}
                      onChange={(tags) => patch({ selectedCapabilityTags: tags })}
                      hasError={fe('selectedCapabilityTags', fieldError).$hasError}
                      errorMessage={fe('selectedCapabilityTags', fieldError).msg}
                    />
                  </Field>
                )}

                {isTransformer && (
                  <Field $span2>
                    <GarageTagSelector
                      fieldId="selectedProjectTags"
                      label="Project type tags"
                      hint="Transformer projects use a staged intake flow — no instant booking."
                      options={GARAGE_PROJECT_TAGS}
                      selected={form.selectedProjectTags}
                      onChange={(tags) => patch({ selectedProjectTags: tags })}
                      hasError={fe('selectedProjectTags', fieldError).$hasError}
                      errorMessage={fe('selectedProjectTags', fieldError).msg}
                    />
                  </Field>
                )}

                {isClusterGarage && (form.selectedClusters || []).length > 0 && (
                  <Field $span2>
                    <GarageServicePreview
                      companyType={form.companyType}
                      selectedClusters={form.selectedClusters}
                      fuelSpecialistTypes={form.fuelSpecialistTypes}
                    />
                  </Field>
                )}

                <Field $span2>
                  <GarageFulfillmentFlags
                    value={form.fulfillmentFlags}
                    onChange={(updates) => patch(updates)}
                  />
                </Field>
              </FieldGrid>
            </>
          ) : (
            <>
          <SectionLabel>Business details</SectionLabel>
          <FieldGrid $twoCol>
            <Field $span2>
              <FieldLabel htmlFor="companyType">Company type *</FieldLabel>
              <Select id="companyType" name="companyType" value={form.companyType} onChange={handleField} $hasError={fe('companyType', fieldError).$hasError}>
                <option value="">Select type</option>
                {companyTypes.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Select>
              <FieldErrorHint message={fe('companyType', fieldError).msg} />
              {selectedCompanyType?.desc && !fe('companyType', fieldError).msg && (
                <FieldHint><span>{selectedCompanyType.desc}</span></FieldHint>
              )}
            </Field>
            <Field>
              <FieldLabel htmlFor="shopName">Shop name *</FieldLabel>
              <FieldInput id="shopName" name="shopName" value={form.shopName} onChange={handleField} $hasError={fe('shopName', fieldError).$hasError} />
              <FieldErrorHint message={fe('shopName', fieldError).msg} />
            </Field>
            {registeredType ? (
              <Field>
                <FieldLabel htmlFor="companyName">Legal company name *</FieldLabel>
                <FieldInput id="companyName" name="companyName" value={form.companyName} onChange={handleField} $hasError={fe('companyName', fieldError).$hasError} />
                <FieldErrorHint message={fe('companyName', fieldError).msg} />
              </Field>
            ) : (
              <Field>
                <FieldLabel htmlFor="ownerName">Owner name *</FieldLabel>
                <FieldInput id="ownerName" name="ownerName" value={form.ownerName} onChange={handleField} $hasError={fe('ownerName', fieldError).$hasError} />
                <FieldErrorHint message={fe('ownerName', fieldError).msg} />
              </Field>
            )}
            {registeredType && (
              <Field>
                <FieldLabel htmlFor="ownerName">Owner name *</FieldLabel>
                <FieldInput id="ownerName" name="ownerName" value={form.ownerName} onChange={handleField} $hasError={fe('ownerName', fieldError).$hasError} />
                <FieldErrorHint message={fe('ownerName', fieldError).msg} />
              </Field>
            )}
            <Field>
              <FieldLabel htmlFor="panNumber">PAN card number *</FieldLabel>
              <FieldInput id="panNumber" name="panNumber" maxLength={10} value={form.panNumber} onChange={handleField} style={{ textTransform: 'uppercase' }} $hasError={fe('panNumber', fieldError).$hasError} />
              <FieldErrorHint message={fe('panNumber', fieldError).msg} />
            </Field>
            <Field>
              <FieldLabel htmlFor="shopNumber">Shop phone *</FieldLabel>
              <FieldInput id="shopNumber" name="shopNumber" inputMode="numeric" maxLength={10} value={form.shopNumber} onChange={handleField} $hasError={fe('shopNumber', fieldError).$hasError} />
              <FieldErrorHint message={fe('shopNumber', fieldError).msg} />
            </Field>
            <Field $span2>
              <FieldLabel htmlFor="gstNumber">GST number</FieldLabel>
              <FieldInput id="gstNumber" name="gstNumber" value={form.gstNumber} onChange={handleField} placeholder="Optional here" $hasError={fe('gstNumber', fieldError).$hasError} />
              <FieldErrorHint message={fe('gstNumber', fieldError).msg} />
              {!fe('gstNumber', fieldError).msg && (
                <FieldHint>Not mandatory at this step unless required for your company type</FieldHint>
              )}
            </Field>
            <Field $span2>
              <FieldLabel htmlFor="businessSpecifications">Categories *</FieldLabel>
              <SpecificationsDropdown
                id="businessSpecifications"
                options={specificationOptions}
                value={selectedSpecifications}
                onChange={handleSpecificationsPatch}
                hasError={fe('businessSpecifications', fieldError).$hasError}
                errorMessage={fe('businessSpecifications', fieldError).msg}
              />
            </Field>
            {hasOtherSpecification && (
              <Field $span2>
                <FieldLabel htmlFor="businessSpecificationOther">Specify other category *</FieldLabel>
                <FieldInput
                  id="businessSpecificationOther"
                  name="businessSpecificationOther"
                  value={form.businessSpecificationOther}
                  onChange={handleField}
                  placeholder="Describe your business category"
                  $hasError={fe('businessSpecificationOther', fieldError).$hasError}
                />
                <FieldErrorHint message={fe('businessSpecificationOther', fieldError).msg} />
              </Field>
            )}
          </FieldGrid>
            </>
          )}
        </Panel>
      )}

      {subStep === 2 && (
        <Panel key="docs">
          <SectionLabel>Business &amp; bank details</SectionLabel>

          <Field>
            <FieldLabel htmlFor={gstFieldId}>GST number *</FieldLabel>
            <FieldInput
              id={gstFieldId}
              name="gstNumber"
              value={form.gstNumber}
              onChange={handleField}
              style={{ textTransform: 'uppercase' }}
              $hasError={fe(gstFieldId, fieldError).$hasError}
            />
            <FieldErrorHint message={fe(gstFieldId, fieldError).msg} />
          </Field>

          <BankTitle>Bank details</BankTitle>
          {isGarageAccount && (
            <FieldHint>
              <span>Account holder name must match to garage name.</span>
            </FieldHint>
          )}
          {!isGarageAccount && sellerCompanySignup && (
            <FieldHint>
              <span>Account holder name must match your shop or business name.</span>
            </FieldHint>
          )}
          <FieldGrid>
            <Field>
              <FieldLabel htmlFor="bankAccountHolder">Account holder name *</FieldLabel>
              <FieldInput
                id="bankAccountHolder"
                name="bankAccountHolder"
                value={form.bankAccountHolder}
                onChange={handleField}
                $hasError={fe('bankAccountHolder', fieldError).$hasError}
              />
              <FieldErrorHint message={fe('bankAccountHolder', fieldError).msg} />
            </Field>
            <Field>
              <FieldLabel htmlFor="bankAccountNumber">Account number *</FieldLabel>
              <FieldInput id="bankAccountNumber" name="bankAccountNumber" value={form.bankAccountNumber} onChange={handleField} $hasError={fe('bankAccountNumber', fieldError).$hasError} />
              <FieldErrorHint message={fe('bankAccountNumber', fieldError).msg} />
            </Field>
            <Field>
              <FieldLabel htmlFor="bankName">Bank name *</FieldLabel>
              <Select
                id="bankName"
                name="bankName"
                value={form.bankName}
                onChange={handleBankChange}
                $hasError={fe('bankName', fieldError).$hasError}
              >
                <option value="">Select bank</option>
                {form.bankName && !INDIAN_BANKS.includes(form.bankName) && (
                  <option value={form.bankName}>{form.bankName}</option>
                )}
                {INDIAN_BANKS.map((bank) => (
                  <option key={bank} value={bank}>{bank}</option>
                ))}
              </Select>
              <FieldErrorHint message={fe('bankName', fieldError).msg} />
            </Field>
            <Field>
              <FieldLabel htmlFor="bankIfsc">IFSC code *</FieldLabel>
              <FieldInput
                id="bankIfsc"
                name="bankIfsc"
                value={form.bankIfsc}
                onChange={handleField}
                disabled={!form.bankName?.trim()}
                placeholder={form.bankName?.trim() ? 'Enter 11-character IFSC code' : 'Select bank first'}
                style={{ textTransform: 'uppercase' }}
                $hasError={fe('bankIfsc', fieldError).$hasError}
              />
              <FieldErrorHint message={fe('bankIfsc', fieldError).msg} />
            </Field>
          </FieldGrid>

          <SectionLabel>Documents &amp; photos</SectionLabel>

          {registeredType && (
            <LocalFileField
              label="Registration certificate"
              fieldId="registrationCertificate"
              value={form.registrationCertificate}
              onChange={(v) => patch({ registrationCertificate: v })}
              errorMessage={fe('registrationCertificate', fieldError).msg}
              required
            />
          )}

          <LocalFileField
            label={`${isGarageAccount ? 'Garage' : 'Shop'} picture`}
            fieldId="shopPicture"
            value={form.shopPicture}
            onChange={(v) => patch({ shopPicture: v })}
            errorMessage={fe('shopPicture', fieldError).msg}
            accept="image/*"
            cameraOnly
            required
          />
          {(!mobileDocFlow || shopPictureDone) && (
            <LocalFileField
              label={`${isGarageAccount ? 'Garage' : 'Company'} / street picture`}
              fieldId="streetPicture"
              value={form.streetPicture}
              onChange={(v) => patch({ streetPicture: v })}
              errorMessage={fe('streetPicture', fieldError).msg}
              accept="image/*"
              cameraOnly
              required
            />
          )}
          {mobileDocFlow && !shopPictureDone && (
            <FieldHint>Complete the first photo before taking the street picture.</FieldHint>
          )}

          {(registeredType || sellerCompanySignup) && (
            <LocalFileField
              label="PAN card picture"
              fieldId="panCardDocument"
              value={form.panCardDocument}
              onChange={(v) => patch({ panCardDocument: v })}
              errorMessage={fe('panCardDocument', fieldError).msg}
              accept="image/*"
              required
            />
          )}

          {sellerCompanySignup && (
            <>
              <LocalFileField
                label="Bank passbook (first page)"
                fieldId="bankPassbook"
                value={form.bankPassbook}
                onChange={(v) => patch({ bankPassbook: v })}
                errorMessage={fe('bankPassbook', fieldError).msg}
                accept="image/*"
                required
              />
              <LocalFileField
                label="Aadhaar card picture"
                fieldId="aadhaarFront"
                value={form.aadhaarFront}
                onChange={(v) => patch({ aadhaarFront: v })}
                errorMessage={fe('aadhaarFront', fieldError).msg}
                accept="image/*"
                required
              />
            </>
          )}
        </Panel>
      )}

      <BtnRow>
        <BackBtn type="button" onClick={goBack}>← Back</BackBtn>
        <ContinueBtn type="button" onClick={goNext}>
          {subStep < 2 ? 'Continue →' : 'Next: Create account →'}
        </ContinueBtn>
      </BtnRow>
    </Wrap>
  );
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  transform: none;

  @media (max-width: 820px) {
    flex: none;
    min-height: auto;
    overflow: visible;
  }
`;

const SubStepBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 18px;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

const SubStepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: ${(p) => (p.$active ? 'var(--pp-text)' : 'var(--pp-muted)')};
`;

const SubDot = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  background: ${(p) => (p.$active || p.$done ? 'var(--pp-brand)' : 'var(--pp-border)')};
  color: ${(p) => (p.$active || p.$done ? '#fff' : 'var(--pp-muted)')};
`;

const Panel = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 4px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  transform: none;
  will-change: scroll-position;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 820px) {
    overflow: visible;
    min-height: auto;
    flex: none;
    will-change: auto;
  }
`;

const SectionLabel = styled.p`
  font-size: 11px;
  font-weight: 600;
  color: var(--pp-muted);
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const FieldGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: ${({ $twoCol }) => ($twoCol ? '1fr 1fr' : '1fr')};

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  margin-bottom: 4px;
  grid-column: ${({ $span2 }) => ($span2 ? '1 / -1' : 'auto')};
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: var(--pp-text);
  margin-bottom: 6px;
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 11px 12px;
  border: 1px solid ${(p) => (p.$hasError ? 'var(--pp-error-border)' : 'var(--pp-border)')};
  border-radius: var(--pp-radius-btn);
  font-size: 14px;
  color: var(--pp-text);
  background: var(--pp-surface);
  box-sizing: border-box;

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px var(--pp-surface) inset !important;
    box-shadow: 0 0 0 1000px var(--pp-surface) inset !important;
    -webkit-text-fill-color: var(--pp-text) !important;
    caret-color: var(--pp-text);
    transition: background-color 9999s ease-out 0s;
  }

  &:focus {
    outline: none;
    border-color: ${(p) => (p.$hasError ? 'var(--pp-error-text)' : 'var(--pp-brand)')};
    box-shadow: ${(p) => (p.$hasError ? 'none' : '0 0 0 3px var(--pp-brand-bg)')};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 11px 12px;
  border: 1px solid var(--pp-border);
  border-radius: var(--pp-radius-btn);
  font-size: 14px;
  color: var(--pp-text);
  background: var(--pp-surface);
  resize: vertical;
  min-height: 96px;
  box-sizing: border-box;
  font-family: inherit;

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px var(--pp-surface) inset !important;
    box-shadow: 0 0 0 1000px var(--pp-surface) inset !important;
    -webkit-text-fill-color: var(--pp-text) !important;
  }

  &:focus {
    outline: none;
    border-color: var(--pp-brand);
    box-shadow: 0 0 0 3px var(--pp-brand-bg);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 11px 36px 11px 12px;
  border: 1px solid ${(p) => (p.$hasError ? 'var(--pp-error-border)' : 'var(--pp-border)')};
  border-radius: var(--pp-radius-btn);
  font-size: 14px;
  color: var(--pp-text);
  background: var(--pp-surface);
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%230891b2' d='M2 4l4 4 4-4'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px var(--pp-surface) inset !important;
    box-shadow: 0 0 0 1000px var(--pp-surface) inset !important;
    -webkit-text-fill-color: var(--pp-text) !important;
  }

  &:focus {
    outline: none;
    border-color: ${(p) => (p.$hasError ? 'var(--pp-error-text)' : 'var(--pp-brand)')};
    box-shadow: ${(p) => (p.$hasError ? 'none' : '0 0 0 3px var(--pp-brand-bg)')};
  }
`;

const FieldHint = styled.p`
  font-size: 11px;
  color: ${(p) => (p.$error ? 'var(--pp-error-text)' : p.$success ? 'var(--pp-success-text, #059669)' : 'var(--pp-muted)')};
  margin: 4px 0 0;
  display: block;
  line-height: 1.4;
`;

const LocationHint = styled.p`
  font-size: 13px;
  color: var(--pp-muted);
  margin: 0 0 14px;
  line-height: 1.45;
`;

const LocationBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  margin: 0 0 14px;
  border: 1px solid var(--pp-brand-border);
  border-radius: var(--pp-radius-btn);
  background: var(--pp-brand-bg);
  color: var(--pp-brand);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  ${({ $top }) => $top && `
    padding: 12px 20px;
    margin-bottom: 12px;
  `}

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const LocationNotice = styled.p`
  font-size: 12px;
  color: ${(p) => (p.$https ? 'var(--pp-error-text, #b91c1c)' : 'var(--pp-muted)')};
  margin: 0 0 14px;
  padding: 10px 12px;
  background: ${(p) => (p.$https ? 'var(--pp-error-bg, #fef2f2)' : 'var(--pp-warn-bg, #fffbeb)')};
  border: 1px solid ${(p) => (p.$https ? 'var(--pp-error-border, #fecaca)' : 'var(--pp-warn-border, #fde68a)')};
  border-radius: var(--pp-radius-btn);
  line-height: 1.45;

  strong {
    font-weight: 700;
  }
`;

const CapturedLocation = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: var(--pp-text);
  margin: 0 0 14px;
  padding: 10px 12px;
  background: var(--pp-brand-bg);
  border: 1px solid var(--pp-brand-border);
  border-radius: var(--pp-radius-btn);

  strong {
    font-weight: 600;
  }

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: var(--pp-brand);
  }
`;

const UploadZone = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 72px;
  padding: 12px;
  border: 2px dashed ${(p) => (p.$hasError ? 'var(--pp-error-border)' : 'var(--pp-border)')};
  border-radius: var(--pp-radius-btn);
  background: ${(p) => (p.$hasError ? 'var(--pp-error-bg)' : 'var(--pp-brand-bg)')};
  color: var(--pp-muted);
  font-size: 13px;
  box-sizing: border-box;

  ${({ $filled }) => $filled && `
    align-items: stretch;
    justify-content: stretch;
    padding: 10px;
    border-style: solid;
  `}

  &:focus {
    outline: 2px solid ${(p) => (p.$hasError ? 'var(--pp-error-text)' : 'var(--pp-brand)')};
    outline-offset: 2px;
  }
`;

const UploadFilled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  width: 100%;
`;

const UploadPreview = styled.img`
  display: block;
  width: 100%;
  max-height: 200px;
  object-fit: contain;
  border-radius: 8px;
  background: var(--pp-surface);
  border: 1px solid var(--pp-border);
`;

const UploadSavedBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 72px;
  padding: 12px;
  border-radius: 8px;
  background: var(--pp-success-bg, #ecfdf5);
  border: 1px solid var(--pp-success-border, #a7f3d0);
  color: var(--pp-success-text, #047857);
  font-size: 14px;
  font-weight: 600;

  svg {
    flex-shrink: 0;
  }
`;

const UploadFileIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 72px;
  font-size: 28px;
  color: var(--pp-brand);
  background: var(--pp-surface);
  border: 1px solid var(--pp-border);
  border-radius: 8px;
`;

const UploadMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
`;

const UploadMetaActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const UploadActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const UploadActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  padding: 10px 14px;
  border: 1px solid var(--pp-border);
  border-radius: var(--pp-radius-btn);
  background: var(--pp-surface);
  color: var(--pp-text);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  box-sizing: border-box;

  svg {
    color: var(--pp-brand);
    flex-shrink: 0;
  }

  &:active {
    background: var(--pp-brand-bg);
    border-color: var(--pp-brand);
  }
`;

const ChangeBtn = styled.button`
  padding: 6px 10px;
  border: 1px solid var(--pp-border);
  background: var(--pp-surface);
  color: var(--pp-text);
  border-radius: 6px;
  font-size: 11px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  svg {
    color: var(--pp-brand);
  }
`;

const UploadFileName = styled.span`
  font-size: 13px;
  color: var(--pp-text);
  word-break: break-all;
`;

const HiddenInput = styled.input` display: none; `;

const UploadRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ClearBtn = styled.button`
  padding: 4px 8px;
  border: none;
  background: var(--pp-error-bg);
  color: var(--pp-error-text);
  border-radius: 6px;
  font-size: 11px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const BankTitle = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: var(--pp-text);
  margin: 16px 0 10px;
`;

const ErrorBox = styled.div`
  background: var(--pp-error-bg);
  color: var(--pp-error-text);
  border: 1px solid var(--pp-error-border);
  border-radius: var(--pp-radius-card);
  padding: 10px 14px;
  font-size: 13px;
  margin-bottom: 14px;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: auto;
  padding: 14px 0 6px;
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  z-index: 6;
  background: var(--pp-surface);
  border-top: 1px solid var(--pp-border);
  box-shadow: 0 -6px 16px rgba(15, 23, 42, 0.06);

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    gap: 8px;
    padding-top: 12px;
    padding-bottom: max(6px, env(safe-area-inset-bottom, 0px));
  }
`;

const BackBtn = styled.button`
  flex: 0 0 auto;
  padding: 12px 16px;
  border: 1px solid var(--pp-border);
  border-radius: var(--pp-radius-btn);
  background: var(--pp-surface);
  color: var(--pp-muted);
  font-size: 14px;
  cursor: pointer;

  @media (max-width: 480px) {
    width: 100%;
    text-align: center;
  }
`;

const ContinueBtn = styled.button`
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: var(--pp-radius-btn);
  background: var(--pp-brand);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;
